import { Restaurant } from 'schema-dts'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { mergeSchedules, schemaOrgOpeninghours } from 'src/lib/js/schedule'

import { getImageUrl } from 'shared/components/common/Image'
import { SiteData, SiteRestaurantLocation } from 'shared/js/types'

import { ScheduleType } from 'public/components/online_ordering/types'

type RestaurantSchemaInput = {
  restaurantName: string
  restaurantPhone: string | undefined
  country: string | null | undefined
  currency: string | null | undefined
  selectedLocation: SiteRestaurantLocation
  locationSchedule: ScheduleType | undefined
  site: Pick<SiteData, 'meta' | 'config'>
  locationUrl: string
  homeUrl: string
  orderUrl?: string | null
  lastPublishedAt?: string | null
  googlePlaceId?: string | null
  giftCardPurchaseUrl?: string | null
}

export const getSameAsUrls = (
  urls: string[] | undefined,
  placeId: string | null | undefined
): string[] | undefined => {
  const gbpUrl = placeId
    ? [
        `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(
          placeId
        )}`
      ]
    : []
  const sameAs = [...new Set([...(urls || []), ...gbpUrl])]
  return sameAs.length ? sameAs : undefined
}

// Tells search engines and AI agents which actions this restaurant supports
// (ordering, reservations, gift cards) with direct links to each.
export const buildPotentialActions = (
  orderUrl: string | null | undefined,
  reservationsUrl: string | null | undefined,
  giftCardPurchaseUrl: string | null | undefined
): Partial<Pick<Restaurant, 'potentialAction'>> => {
  const potentialAction = [
    ...(orderUrl
      ? [{ '@type': 'OrderAction' as const, target: orderUrl }]
      : []),
    ...(reservationsUrl
      ? [{ '@type': 'ReserveAction' as const, target: reservationsUrl }]
      : []),
    ...(giftCardPurchaseUrl
      ? [{ '@type': 'BuyAction' as const, target: giftCardPurchaseUrl }]
      : [])
  ]

  return potentialAction.length ? { potentialAction } : {}
}

export const buildBaseRestaurantSchema = ({
  restaurantName,
  restaurantPhone,
  country,
  currency,
  selectedLocation,
  locationSchedule,
  site,
  locationUrl,
  homeUrl,
  orderUrl,
  lastPublishedAt,
  googlePlaceId,
  giftCardPurchaseUrl
}: RestaurantSchemaInput): Omit<Restaurant, 'hasMenu'> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    '@id': locationUrl,
    url: locationUrl || homeUrl,
    sameAs: getSameAsUrls(site.meta?.urls, googlePlaceId),
    name: restaurantName,
    description: site.meta?.description,
    servesCuisine: site.meta?.cuisine,
    paymentAccepted: site.meta?.acceptedPaymentTypes,
    currenciesAccepted: currency || undefined,
    priceRange: site.meta?.priceRange,
    image: site.meta.icon ? getImageUrl(site.meta.icon) : undefined,
    logo: site.meta.icon ? getImageUrl(site.meta.icon) : undefined,
    address: {
      '@type': 'PostalAddress',
      addressCountry: country || undefined,
      postalCode: selectedLocation?.zipcode,
      addressRegion:
        selectedLocation?.administrativeArea || selectedLocation?.state,
      addressLocality: selectedLocation?.city,
      streetAddress: selectedLocation?.address2
        ? `${selectedLocation?.address1}, ${selectedLocation?.address2}`
        : selectedLocation?.address1
    },
    ...(selectedLocation?.lat && selectedLocation?.long
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            longitude: `${selectedLocation?.long}`,
            latitude: `${selectedLocation?.lat}`
          }
        }
      : {}),
    ...(selectedLocation.reservationsUrl
      ? { acceptsReservations: !!selectedLocation.reservationsUrl }
      : {}),
    ...buildPotentialActions(
      orderUrl,
      selectedLocation.reservationsUrl,
      giftCardPurchaseUrl
    ),
    telephone: selectedLocation.phoneNumber || restaurantPhone,
    openingHoursSpecification: schemaOrgOpeninghours(
      mergeSchedules(
        selectedLocation,
        locationSchedule,
        !!site.config.isOnlineOrderingOnly,
        DiningOptionBehavior.TakeOut
      )
    ),
    ...(lastPublishedAt ? { dateModified: lastPublishedAt } : {})
  }
}
